import { useState, type ComponentPropsWithRef } from 'react';
import { cn } from '@/shared/lib/cn';
import { useFormFieldContext } from './form-field-context';

export interface TextareaProps extends ComponentPropsWithRef<'textarea'> {
  /** Pokazuje licznik znakow w prawym dolnym rogu. Wymaga `maxLength`. */
  showCounter?: boolean;
  invalid?: boolean;
}

/**
 * Pole tekstowe wielowierszowe, opcjonalnie z licznikiem znakow —
 * jak „Uwagi do meczu (opcjonalnie) … 0/250”.
 *
 * Licznik dziala zarowno dla pola sterowanego, jak i niesterowanego:
 * gdy rodzic nie podaje `value`, komponent liczy znaki samodzielnie.
 */
export function Textarea({
  className,
  showCounter = false,
  maxLength,
  invalid,
  id,
  value,
  defaultValue,
  onChange,
  rows = 4,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  required,
  ...props
}: TextareaProps) {
  const field = useFormFieldContext();
  const resolvedInvalid = invalid ?? ariaInvalid ?? field?.invalid ?? false;

  const isControlled = value !== undefined;
  const [internalLength, setInternalLength] = useState(() => String(defaultValue ?? '').length);
  const length = isControlled ? String(value).length : internalLength;

  return (
    <div className="relative">
      <textarea
        id={id ?? field?.controlId}
        rows={rows}
        maxLength={maxLength}
        aria-describedby={ariaDescribedBy ?? field?.describedBy}
        aria-invalid={resolvedInvalid || undefined}
        required={required ?? field?.required}
        {...(isControlled ? { value } : { defaultValue })}
        onChange={(event) => {
          if (!isControlled) setInternalLength(event.target.value.length);
          onChange?.(event);
        }}
        className={cn(
          'bg-surface-raised text-fg border-border-input w-full resize-y rounded-lg border',
          'placeholder:text-fg-subtle px-3.5 py-2.5 text-sm',
          'transition-[border-color,box-shadow] duration-150',
          'focus-visible:border-brand focus-visible:ring-brand/35 focus-visible:ring-2 focus-visible:outline-none',
          'disabled:bg-surface-sunken disabled:text-fg-disabled disabled:cursor-not-allowed',
          'aria-invalid:border-danger-500 aria-invalid:focus-visible:ring-danger-500/30',
          showCounter && maxLength ? 'pb-7' : '',
          className,
        )}
        {...props}
      />

      {showCounter && maxLength && (
        <span
          aria-hidden="true"
          className="text-fg-subtle pointer-events-none absolute right-3 bottom-2.5 text-xs tabular-nums"
        >
          {length}/{maxLength}
        </span>
      )}
    </div>
  );
}
